import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import { useStore } from '../../../../store';
import { useMapContext } from '../context/map-context';
import 'leaflet-imageoverlay-rotated';

export const ImageOverlay = (): null => {
  const map = useMap();
  const { mapImageImage, mapOrigin, mapImage } = useStore();
  const {
    configurationValues, dimensions, imageAngle, rangePerDegree,
  } = useMapContext();

  const [base64, setBase64] = useState<string | null>(null);
  const [zeroPoint, setZeroPoint] = useState<[number, number]>([
    Number(mapOrigin.data.latitude),
    Number(mapOrigin.data.longitude),
  ]);

  const imageOverlayRef = useRef<L.ImageOverlay | null>(null);

  // Convert Blob to Base64
  const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    if (!(blob instanceof Blob)) return;
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  useEffect(() => {
    if (!mapImageImage.data) return;

    blobToBase64(mapImageImage.data as Blob)
      .then((base64String) => setBase64(base64String))
      // eslint-disable-next-line no-console
      .catch((error) => console.error('Error converting blob to base64:', error));
  }, [mapImageImage.data]);

  useEffect(() => {
    setZeroPoint([Number(mapOrigin.data.latitude), Number(mapOrigin.data.longitude)]);
  }, [mapOrigin.data]);

  // Add the image overlay
  useEffect(() => {
    if (!base64 || !dimensions?.width || !dimensions.height || !rangePerDegree) return;

    if (imageOverlayRef.current) {
      map.removeLayer(imageOverlayRef.current);
    }

    const { xofffset, yoffset, pixelsPerLength } = mapImage.data;

    const x = Number(dimensions?.width) - Number(xofffset);
    const y = Number(dimensions?.height) - Number(yoffset);

    const xLengthInFt1 = (1 / Number(pixelsPerLength)) * Number(xofffset); // x1
    const yLengthInFt1 = (1 / Number(pixelsPerLength)) * Number(yoffset); // y1

    const xLengthInFt2 = (1 / Number(pixelsPerLength)) * Number(x); // x2
    const yLengthInFt2 = (1 / Number(pixelsPerLength)) * Number(y); // y2
    // becouse in backend lat and lon are mixed
    const rangePerDegreeLat = rangePerDegree.lon;
    const rangePerDegreeLon = rangePerDegree.lat;

    const latOffset = xLengthInFt1 / rangePerDegreeLat;
    const lonOffset = yLengthInFt1 / rangePerDegreeLon;

    const latOffset2 = xLengthInFt2 / rangePerDegreeLat;
    const lonOffset2 = yLengthInFt2 / rangePerDegreeLon;

    function rotatePoint(
      point: [number, number],
      center: [number, number],
      angle: number,
    ): [number, number] {
      const angleRad = (angle * Math.PI) / 180;

      // Project lat/lng to pixel coordinates
      const pointProjected = map.project(L.latLng(point));
      const centerProjected = map.project(L.latLng(center));

      const deltaX = pointProjected.x - centerProjected.x;
      const deltaY = pointProjected.y - centerProjected.y;

      // Rotate using trigonometric formulas
      const rotatedX = deltaX * Math.cos(angleRad) - deltaY * Math.sin(angleRad);
      const rotatedY = deltaX * Math.sin(angleRad) + deltaY * Math.cos(angleRad);

      // Translate back to pixel coordinates and then to lat/lng
      const rotatedPointProjected = L.point(
        centerProjected.x + rotatedX,
        centerProjected.y + rotatedY,
      );

      return map.unproject(rotatedPointProjected);
    }

    const topLeft: [number, number] = [zeroPoint[0] + lonOffset2, zeroPoint[1] - latOffset];
    const topRight: [number, number] = [zeroPoint[0] + lonOffset2, zeroPoint[1] + latOffset2];
    const bottomLeft: [number, number] = [zeroPoint[0] - lonOffset, zeroPoint[1] - latOffset];
    // const bottomRight: [number, number] = [zeroPoint[0] - lonOffset, zeroPoint[1] + latOffset2];
    const rotatedTopLeft = rotatePoint(topLeft, zeroPoint, imageAngle);
    const rotatedTopRight = rotatePoint(topRight, zeroPoint, imageAngle);
    const rotatedBottomLeft = rotatePoint(bottomLeft, zeroPoint, imageAngle);
    // const rotatedBottomRight = rotatePoint(bottomRight, zeroPoint, imageAngle);

    if (imageOverlayRef.current) {
      imageOverlayRef.current.remove();
    }

    imageOverlayRef.current = L.imageOverlay.rotated(
      base64,
      rotatedTopLeft,
      rotatedTopRight,
      rotatedBottomLeft,
      {
        opacity: (1 - configurationValues.transparency?.image) ?? 0.5,
      },
    ).addTo(map);

    // eslint-disable-next-line consistent-return
    return () => {
      if (imageOverlayRef.current) {
        map.removeLayer(imageOverlayRef.current);
        imageOverlayRef.current = null;
      }
    };
  }, [base64, configurationValues.transparency?.image,
    map, mapImage.data, zeroPoint, dimensions, imageAngle]);

  return null;
};
